import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
    Chip
} from '@mui/material'
import { Icon } from '@iconify/react'
import { useEffect, useState } from 'react'
import axios from 'axios'
import apiUrl from '../../configs/constant'
import { useToast } from '../../components/ToastProvider'

/* =========================
   TYPES
   ========================= */

type Summary = {
    id: number
    kode: string
    name: string
    hex_color: string
    icon: string
}

type Props = {
    open: boolean
    contact?: {
        id: number
        sdcust_id: number
        summary?: Summary | null
    }
    onClose: () => void
    onReload: () => void
}

/* =========================
   COMPONENT
   ========================= */

export default function DialogRingkasan({
    open,
    contact,
    onClose,
    onReload
}: Props) {
    const { showToast } = useToast()

    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token

    const [listSummary, setListSummary] = useState<Summary[]>([])
    const [selectedSummary, setSelectedSummary] = useState<Summary | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (open) {
            getSummary()
        }
    }, [open])

    useEffect(() => {
        if (open && contact?.summary) {
            setSelectedSummary(contact.summary)
        }
    }, [open, contact])

    const getSummary = async () => {
        try {
            const res = await axios.get(
                `${apiUrl.apiUrl}/omni/chat/get-summary`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            if (res.data.success) {
                setListSummary(res.data.data)
            } else {
                showToast(res.data.message, 'error')
            }
        } catch {
            showToast('Gagal mengambil data ringkasan', 'error')
        }
    }

    const saveSummary = async () => {
        if (!contact || !selectedSummary) return

        setLoading(true)

        try {
            const payload = {
                sdcust_id: contact.sdcust_id,
                sdsummary_id: selectedSummary.id
            }

            const res = await axios.post(
                `${apiUrl.apiUrl}/omni/chat/save-summary`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            if (res.data.success) {
                showToast('Ringkasan berhasil disimpan', 'success')
                onClose()
                onReload()
            } else {
                showToast(res.data.message, 'error')
            }
        } catch {
            showToast('Gagal menyimpan ringkasan', 'error')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Ringkasan Customer</DialogTitle>

            <DialogContent dividers>
                {!contact ? (
                    <Typography>Pilih contact terlebih dahulu</Typography>
                ) : (
                    <RadioGroup
                        value={selectedSummary?.id ?? ''}
                        onChange={(e) => {
                            const id = Number(e.target.value)
                            const summary = listSummary.find(s => s.id === id) || null
                            setSelectedSummary(summary)
                        }}
                    >
                        <Stack spacing={1}>
                            {listSummary.map(item => (
                                <FormControlLabel
                                    key={item.id}
                                    value={item.id}
                                    control={<Radio disabled={loading} />}
                                    label={
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                icon={<Icon icon={item?.icon || ''} color='#fff' />}
                                                label={item?.name}
                                                sx={{
                                                    bgcolor: item?.hex_color,
                                                    color: '#fff',
                                                    fontWeight: 600,
                                                    borderRadius: 1,
                                                    mr: 1
                                                }}
                                            />
                                        </Stack>
                                    }
                                />
                            ))}
                        </Stack>
                    </RadioGroup>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Tutup
                </Button>
                <Button
                    variant="contained"
                    onClick={saveSummary}
                    disabled={!contact || !selectedSummary || loading}
                >
                    Simpan
                </Button>
            </DialogActions>
        </Dialog>
    )
}
